/* ==========================================================================
   Triple 3 Music — idempotent section controllers
   Centralized Shopify Theme Editor lifecycle for dynamic sections.
   ========================================================================== */

(() => {
  'use strict';

  /** @type {Map<Element, { destroy: () => void, [key: string]: unknown }>} */
  const instances = new Map();

  const focusableSelector =
    'button:not([disabled]), input:not([disabled]), select:not([disabled]), a[href], summary, [tabindex]:not([tabindex="-1"])';

  /**
   * @param {ParentNode & Partial<Element>} scope
   * @param {string} selector
   * @returns {Element[]}
   */
  function findRoots(scope, selector) {
    const roots = [];
    if (scope.matches?.(selector)) roots.push(/** @type {Element} */ (scope));
    scope.querySelectorAll?.(selector).forEach((root) => roots.push(root));
    return roots;
  }

  /**
   * @param {Element} root
   * @param {() => { destroy: () => void, [key: string]: unknown }} create
   */
  function mount(root, create) {
    if (instances.has(root)) return;
    try {
      instances.set(root, create());
    } catch (error) {
      console.error('Unable to initialize theme section controller:', error);
    }
  }

  /**
   * @param {ParentNode & Partial<Element>} scope
   */
  function init(scope = document) {
    scope.querySelectorAll?.('.rte table').forEach((table) => {
      table.setAttribute('tabindex', '0');
      if (!table.hasAttribute('aria-label') && !table.querySelector('caption')) {
        table.setAttribute('aria-label', window.Triple3App?.getConfig().strings.scrollableTable || 'Scrollable table');
      }
    });
    findRoots(scope, '[data-header-navigation]').forEach((root) => {
      mount(root, () => createHeaderNavigationController(root));
    });
    findRoots(scope, '[data-best-sellers]').forEach((root) => {
      mount(root, () => createTabsController(root));
    });
    findRoots(scope, '[data-flash-section]').forEach((root) => {
      mount(root, () => createCountdownController(root));
    });
    findRoots(scope, '[data-cart-form]').forEach((root) => {
      mount(root, () => createCartController(root));
    });
    findRoots(
      scope,
      '[data-collection-section], [data-search-section]'
    ).forEach((root) => {
      mount(root, () => createFacetsController(root));
    });
    findRoots(scope, '[data-faq-section]').forEach((root) => {
      mount(root, () => createFaqController(root));
    });
    findRoots(scope, '[data-legal-section]').forEach((root) => {
      mount(root, () => createLegalController(root));
    });
    findRoots(scope, '[data-newsletter-popup]').forEach((root) => {
      mount(root, () => createNewsletterPopupController(root));
    });
    findRoots(scope, '.slideshow').forEach((root) => {
      mount(root, () => createSlideshowEditorController(root));
    });
  }

  /**
   * @param {Element} scope
   */
  function destroy(scope) {
    for (const [root, controller] of instances) {
      if (root === scope || scope.contains(root)) {
        controller.destroy();
        instances.delete(root);
      }
    }
  }

  /**
   * @param {Event} event
   * @param {string} method
   */
  function invokeForEvent(event, method) {
    const target = event.target instanceof Element ? event.target : null;
    for (const [root, controller] of instances) {
      if (!target || (root !== target && !root.contains(target) && !target.contains(root))) {
        continue;
      }
      const callback = controller[method];
      if (typeof callback === 'function') {
        callback.call(controller, event);
      }
    }
  }

  /**
   * Native disclosure navigation; links retain normal browser navigation.
   * @param {Element} root
   * @returns {{ destroy: () => void }}
   */
  function createHeaderNavigationController(root) {
    const abortController = new AbortController();
    const options = { signal: abortController.signal };
    const header = root.closest('.shopify-section-group-header-group') || root;
    const updateHeight = () => {
      const height = getComputedStyle(header).position === 'sticky' ? header.getBoundingClientRect().height : 0;
      const nextHeight = `${Math.ceil(height)}px`;
      if (document.documentElement.style.getPropertyValue('--sticky-header-offset') !== nextHeight) {
        document.documentElement.style.setProperty('--sticky-header-offset', nextHeight);
      }
    };
    updateHeight();
    const heightObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(updateHeight) : null;
    heightObserver?.observe(header);
    const desktop = window.matchMedia('(min-width: 1024px)');
    const menus = [...root.querySelectorAll('[data-header-menu]')].filter(
      (element) => element instanceof HTMLDetailsElement
    );
    /** @type {Map<HTMLDetailsElement, number>} */
    const timers = new Map();

    /** @param {HTMLDetailsElement} menu */
    function clearTimer(menu) {
      window.clearTimeout(timers.get(menu));
      timers.delete(menu);
    }

    /** @param {HTMLDetailsElement | null} keep */
    function closeOthers(keep = null) {
      menus.forEach((menu) => {
        if (menu === keep) return;
        clearTimer(menu);
        menu.open = false;
      });
    }

    menus.forEach((menu) => {
      const summary = menu.querySelector('summary');
      if (!(summary instanceof HTMLElement)) return;
      summary.addEventListener('click', () => clearTimer(menu), options);
      menu.addEventListener('toggle', () => {
        if (menu.open) closeOthers(menu);
      }, options);
      menu.addEventListener('pointerenter', (event) => {
        if (!desktop.matches || !(event instanceof PointerEvent) || event.pointerType !== 'mouse') return;
        clearTimer(menu);
        timers.set(menu, window.setTimeout(() => {
          timers.delete(menu);
          closeOthers(menu);
          menu.open = true;
        }, 120));
      }, options);
      menu.addEventListener('pointerleave', () => {
        clearTimer(menu);
        timers.set(menu, window.setTimeout(() => {
          timers.delete(menu);
          if (!menu.contains(document.activeElement)) menu.open = false;
        }, 160));
      }, options);
      menu.addEventListener('keydown', (event) => {
        if (!(event instanceof KeyboardEvent)) return;
        if (event.key === 'Escape' && menu.open) {
          event.preventDefault();
          clearTimer(menu);
          menu.open = false;
          summary.focus();
        } else if (event.target === summary && ['ArrowDown', 'ArrowUp'].includes(event.key)) {
          event.preventDefault();
          clearTimer(menu);
          closeOthers(menu);
          menu.open = true;
          const links = [...menu.querySelectorAll('a[href]')].filter(
            (link) => link.getClientRects().length > 0 && getComputedStyle(link).visibility !== 'hidden'
          );
          const link = event.key === 'ArrowUp' ? links.at(-1) : links[0];
          if (link instanceof HTMLElement) link.focus();
        }
      }, options);
    });

    root.addEventListener('focusout', (event) => {
      if (event instanceof FocusEvent && event.relatedTarget instanceof Node && root.contains(event.relatedTarget)) {
        menus.forEach((menu) => {
          if (!menu.contains(event.relatedTarget)) menu.open = false;
        });
      } else {
        closeOthers();
      }
    }, options);
    document.addEventListener('pointerdown', (event) => {
      if (!(event.target instanceof Node) || !root.contains(event.target)) closeOthers();
    }, options);
    desktop.addEventListener('change', () => closeOthers(), options);

    return {
      destroy() {
        abortController.abort();
        closeOthers();
        heightObserver?.disconnect();
      }
    };
  }

  /**
   * @param {Element} root
   */
  function createTabsController(root) {
    const abortController = new AbortController();
    const options = { signal: abortController.signal };
    const tabs = [...root.querySelectorAll('[role="tab"][data-bstab]')];
    const panels = [...root.querySelectorAll('[data-bstab-panel]')];

    /**
     * @param {Element | undefined} tab
     * @param {boolean} focus
     */
    function activate(tab, focus = false) {
      if (!tab) return;
      tabs.forEach((item) => {
        const active = item === tab;
        item.classList.toggle('tab-bar__item--active', active);
        item.setAttribute('aria-selected', String(active));
        item.setAttribute('tabindex', active ? '0' : '-1');
      });
      panels.forEach((panel) => {
        const active = panel.getAttribute('data-bstab-panel') === tab.getAttribute('data-bstab');
        panel.toggleAttribute('hidden', !active);
        panel.toggleAttribute('inert', !active);
        panel.setAttribute('aria-hidden', String(!active));
        if (active) {
          panel.querySelectorAll('.card-product:not(.is-revealed)').forEach((card) => {
            card.classList.add('is-revealed');
          });
        }
      });
      if (focus && 'focus' in tab) tab.focus();
    }

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activate(tab), options);
      tab.addEventListener(
        'keydown',
        (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let nextIndex = index;
          if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = tabs.length - 1;
          activate(tabs[nextIndex], true);
        },
        options
      );
    });

    activate(tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0]);

    return {
      destroy: () => abortController.abort(),
      onBlockSelect(event) {
        const blockId = event.detail?.blockId;
        const selected =
          tabs.find((tab) => tab.getAttribute('data-bstab') === blockId) ||
          (event.target instanceof Element
            ? event.target.closest('[data-bstab]')
            : null);
        activate(selected || undefined);
      }
    };
  }

  /**
   * @param {Element} root
   */
  function createCountdownController(root) {
    const countdown = root.querySelector('[data-flash-countdown]');
    let timer = 0;

    if (countdown) {
      const deadline = Date.parse(countdown.getAttribute('data-deadline') || '');
      const hours = countdown.querySelector('[data-countdown-hours]');
      const minutes = countdown.querySelector('[data-countdown-minutes]');
      const seconds = countdown.querySelector('[data-countdown-seconds]');

      if (!Number.isFinite(deadline) || !hours || !minutes || !seconds) {
        countdown.setAttribute('hidden', '');
      } else {
        const pad = (value) => String(value).padStart(2, '0');
        const tick = () => {
          const difference = deadline - Date.now();
          if (difference <= 0) {
            hours.textContent = '00';
            minutes.textContent = '00';
            seconds.textContent = '00';
            countdown.setAttribute('data-expired', 'true');
            countdown.setAttribute('hidden', '');
            window.clearInterval(timer);
            return;
          }
          hours.textContent = pad(Math.floor(difference / 3600000));
          minutes.textContent = pad(Math.floor((difference % 3600000) / 60000));
          seconds.textContent = pad(Math.floor((difference % 60000) / 1000));
        };
        tick();
        if (!countdown.hasAttribute('hidden')) timer = window.setInterval(tick, 1000);
      }
    }

    return {
      destroy() {
        window.clearInterval(timer);
      }
    };
  }

  /**
   * @param {Element} root
   */
  function createCartController(root) {
    const abortController = new AbortController();
    const options = { signal: abortController.signal };

    /**
     * @param {Element} control
     * @param {number} quantity
     */
    async function updateLine(control, quantity) {
      const row = control.closest('.cart-item');
      const quantityControl = row?.querySelector('.cart-item__quantity');
      if (quantityControl?.getAttribute('data-loading') === 'true') return;
      quantityControl?.setAttribute('data-loading', 'true');
      row?.setAttribute('aria-busy', 'true');

      try {
        const cart = await window.Triple3App?.updateCartItem(
          control.getAttribute('data-line-key'),
          Math.max(0, quantity)
        );
        if (!cart) throw new Error(window.Triple3App?.getConfig().strings.cartError);
        window.location.reload();
      } catch (error) {
        console.error('Unable to update cart line:', error);
        const message =
          error instanceof Error
            ? error.message
            : window.Triple3App?.getConfig().strings.cartError;
        window.Triple3App?.showToast(message);
        const input = row?.querySelector('.cart-item__qty-input');
        if (input instanceof HTMLInputElement) input.value = input.defaultValue;
        quantityControl?.setAttribute('data-loading', 'false');
        row?.removeAttribute('aria-busy');
      }
    }

    root.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element)) return;
        const control = event.target.closest('[data-cart-quantity]');
        if (!control) return;
        updateLine(control, Number(control.getAttribute('data-cart-quantity')));
      },
      options
    );

    root.addEventListener(
      'change',
      (event) => {
        if (!(event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement)) return;
        if (event.target.matches('.cart-item__qty-input')) {
          const quantity = Number(event.target.value);
          if (!event.target.value.trim() || !Number.isSafeInteger(quantity) || quantity < 0) {
            event.target.value = event.target.defaultValue;
            window.Triple3App?.showToast(window.Triple3App.getConfig().strings.cartInvalidQuantity, 'error');
            return;
          }
          updateLine(event.target, quantity);
          return;
        }
        if (!event.target.matches('.cart-summary__note-field')) return;

        const noteField = event.target;
        noteField.disabled = true;
        noteField.setAttribute('aria-busy', 'true');
        window.Triple3App?.updateCartNote(noteField.value).then((cart) => {
          noteField.disabled = false;
          noteField.removeAttribute('aria-busy');
          if (!cart) noteField.focus();
        });
      },
      options
    );

    /** Finds the address form section (KiriminAja app block) that follows the cart section. */
    function findAddressForm() {
      const cartSection = root.closest('.shopify-section');
      const appSection = cartSection?.nextElementSibling;
      if (appSection instanceof HTMLElement && appSection.offsetHeight > 0) return appSection;
      return document.querySelector(
        '[id*="kiriminaja" i], [class*="kiriminaja" i], [data-block-id*="kiriminaja" i]'
      );
    }

    function scrollToAddressForm() {
      const target = findAddressForm();
      if (!(target instanceof HTMLElement)) return;
      const headerOffset = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--sticky-header-offset')
      ) || 0;
      const top = target.getBoundingClientRect().top + window.scrollY - headerOffset - 16;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: Math.max(0, top), behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    document.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element)) return;
        if (!event.target.closest('[data-complete-address]')) return;
        event.preventDefault();
        scrollToAddressForm();
      },
      options
    );

    return {
      destroy: () => abortController.abort()
    };
  }

  /**
   * @param {Element} root
   */
  function createFacetsController(root) {
    const abortController = new AbortController();
    const options = { signal: abortController.signal };
    const facets = root.querySelector('[data-facets]');
    const openButton = root.querySelector('[data-facets-open]');
    const closeButtons = root.querySelectorAll('[data-facets-close]');
    const grid = root.querySelector('[data-product-grid]');
    const mobileQuery = window.matchMedia('(max-width: 1023px)');
    let returnFocus = null;

    function syncAccessibility() {
      if (!facets) return;
      const hidden = mobileQuery.matches && !facets.classList.contains('is-open');
      facets.setAttribute('aria-hidden', String(hidden));
      facets.toggleAttribute('inert', hidden);
    }

    function openFacets() {
      if (!facets) return;
      returnFocus = document.activeElement;
      facets.removeAttribute('inert');
      facets.classList.add('is-open');
      facets.setAttribute('aria-hidden', 'false');
      root.querySelector('.facets-overlay')?.classList.add('is-open');
      openButton?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('overlay-open');
      facets.querySelector('[data-facets-close]')?.focus();
    }

    function closeFacets(restoreFocus = true) {
      if (!facets) return;
      facets.classList.remove('is-open');
      root.querySelector('.facets-overlay')?.classList.remove('is-open');
      openButton?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-open');
      syncAccessibility();
      if (restoreFocus && returnFocus instanceof HTMLElement) returnFocus.focus();
    }

    syncAccessibility();
    openButton?.addEventListener('click', openFacets, options);
    closeButtons.forEach((button) => {
      button.addEventListener('click', () => closeFacets(), options);
    });
    mobileQuery.addEventListener(
      'change',
      () => {
        closeFacets(false);
        syncAccessibility();
      },
      options
    );

    root.querySelectorAll('[data-sort-select]').forEach((select) => {
      select.addEventListener(
        'change',
        () => {
          const hiddenSort = facets?.querySelector('[data-sort-hidden]');
          if (
            hiddenSort instanceof HTMLInputElement &&
            select instanceof HTMLSelectElement
          ) {
            hiddenSort.value = select.value;
          }
          applyFilters();
        },
        options
      );
    });

    root.querySelectorAll('[data-view]').forEach((button) => {
      button.addEventListener(
        'click',
        () => {
          const listView = button.getAttribute('data-view') === 'list';
          grid?.classList.toggle('product-grid--list', listView);
          root.querySelectorAll('[data-view]').forEach((viewButton) => {
            const selected = viewButton === button;
            viewButton.classList.toggle('view-toggle__btn--active', selected);
            viewButton.setAttribute('aria-pressed', String(selected));
          });
        },
        options
      );
    });

    facets?.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape') closeFacets();
        if (event.key !== 'Tab' || !facets.classList.contains('is-open')) return;
        const focusable = [...facets.querySelectorAll(focusableSelector)].filter(
          (element) => element.getClientRects().length > 0
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          if (last instanceof HTMLElement) last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          if (first instanceof HTMLElement) first.focus();
        }
      },
      options
    );

    const sectionId =
      root.getAttribute('data-collection-section') ||
      root.getAttribute('data-search-section') ||
      root.id.replace('shopify-section-', '');
    let filterAbortController = new AbortController();
    let priceDebounceTimer = null;

    async function applyFilters(urlOrString = null, updateHistory = true) {
      if (!facets) return;
      filterAbortController.abort();
      filterAbortController = new AbortController();

      let targetUrl;
      if (typeof urlOrString === 'string' && urlOrString.length > 0) {
        targetUrl = new URL(urlOrString, window.location.origin);
      } else {
        const formData = new FormData(facets);
        const searchParams = new URLSearchParams();
        for (const [key, value] of formData.entries()) {
          if (value !== '') searchParams.append(key, value.toString());
        }
        const actionUrl = facets.getAttribute('action') || window.location.pathname;
        targetUrl = new URL(actionUrl, window.location.origin);
        targetUrl.search = searchParams.toString();
      }

      const requestUrl = new URL(targetUrl.toString());
      if (sectionId) {
        requestUrl.searchParams.set('section_id', sectionId);
      }

      const resultsContainer =
        root.querySelector('[data-collection-results], [data-search-results], .collection-results') ||
        grid;
      resultsContainer?.setAttribute('aria-busy', 'true');
      resultsContainer?.classList.add('is-filtering');

      try {
        const response = await fetch(requestUrl.toString(), {
          signal: filterAbortController.signal,
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        if (!response.ok) {
          window.location.href = targetUrl.toString();
          return;
        }

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // 1. Update Product Grid
        const nextGrid = doc.querySelector('[data-product-grid]');
        if (nextGrid && grid) {
          grid.innerHTML = nextGrid.innerHTML;
          // Trigger lazy images / reveal if needed
          window.Triple3App?.revealCards?.(grid);
        }

        // 2. Update Active Filters
        const nextActive = doc.querySelector('.active-filters');
        const currentActive = root.querySelector('.active-filters');
        if (nextActive && currentActive) {
          currentActive.replaceWith(nextActive);
        } else if (nextActive && !currentActive) {
          const insertTarget = root.querySelector('[data-product-grid]');
          insertTarget?.parentNode?.insertBefore(nextActive, insertTarget);
        } else if (!nextActive && currentActive) {
          currentActive.remove();
        }

        // 3. Update Counts
        const nextCounts = doc.querySelectorAll('.collection-toolbar__count');
        const currentCounts = root.querySelectorAll('.collection-toolbar__count');
        currentCounts.forEach((countEl, i) => {
          if (nextCounts[i]) countEl.innerHTML = nextCounts[i].innerHTML;
        });

        // 4. Update Pagination
        const nextPagination = doc.querySelector('.pagination');
        const currentPagination = root.querySelector('.pagination');
        if (nextPagination && currentPagination) {
          currentPagination.replaceWith(nextPagination);
        } else if (nextPagination && !currentPagination) {
          resultsContainer?.appendChild(nextPagination);
        } else if (!nextPagination && currentPagination) {
          currentPagination.remove();
        }

        // 5. Update Facets Filter Values & Counts while preserving open state
        const nextFacets = doc.querySelector('[data-facets]');
        if (nextFacets && facets) {
          const openDetails = new Set(
            [...facets.querySelectorAll('.facets__disclosure[open]')].map(
              (d) => d.querySelector('summary span')?.textContent?.trim()
            )
          );

          // Update facet items
          const nextDisclosures = nextFacets.querySelectorAll('.facets__disclosure');
          const currentDisclosures = facets.querySelectorAll('.facets__disclosure');
          nextDisclosures.forEach((nextDisc, i) => {
            const currentDisc = currentDisclosures[i];
            if (currentDisc) {
              const label = currentDisc.querySelector('summary span')?.textContent?.trim();
              const nextList = nextDisc.querySelector('.facets__list');
              const currentList = currentDisc.querySelector('.facets__list');
              if (nextList && currentList) {
                currentList.innerHTML = nextList.innerHTML;
              }
              if (openDetails.has(label)) {
                currentDisc.setAttribute('open', '');
              }
            }
          });

          // Sync clear filters action link
          const nextClearLink = nextFacets.querySelector('.facets__actions a');
          const currentClearLink = facets.querySelector('.facets__actions a');
          if (nextClearLink && currentClearLink) {
            currentClearLink.setAttribute('href', nextClearLink.getAttribute('href') || '#');
          }
        }

        // 6. Update URL History
        if (updateHistory) {
          window.history.pushState(
            { url: targetUrl.toString() },
            '',
            targetUrl.toString()
          );
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Real-time filter error:', error);
          window.location.href = targetUrl.toString();
        }
      } finally {
        resultsContainer?.removeAttribute('aria-busy');
        resultsContainer?.classList.remove('is-filtering');
      }
    }

    // Intercept form submit
    facets?.addEventListener('submit', (event) => {
      event.preventDefault();
      applyFilters();
      if (mobileQuery.matches) closeFacets();
    }, options);

    // Real-time checkbox and radio input changes
    facets?.addEventListener('change', (event) => {
      const target = event.target;
      if (
        target instanceof HTMLInputElement &&
        (target.type === 'checkbox' || target.type === 'radio')
      ) {
        applyFilters();
      }
    }, options);

    // Debounced real-time price inputs
    facets?.addEventListener('input', (event) => {
      const target = event.target;
      if (
        target instanceof HTMLInputElement &&
        target.type === 'number'
      ) {
        window.clearTimeout(priceDebounceTimer);
        priceDebounceTimer = window.setTimeout(() => {
          applyFilters();
        }, 500);
      }
    }, options);

    // Real-time active filter tag click, clear-all, and pagination click
    root.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const clearBtn = target?.closest(
        '.active-filters__clear-btn, .active-filters__clear-all, [data-facet-remove], [data-facet-clear-all]'
      );
      if (clearBtn && clearBtn instanceof HTMLAnchorElement && clearBtn.href) {
        event.preventDefault();
        applyFilters(clearBtn.href);
        return;
      }

      const paginationLink = target?.closest('.pagination a, [data-pagination-link]');
      if (paginationLink && paginationLink instanceof HTMLAnchorElement && paginationLink.href) {
        event.preventDefault();
        applyFilters(paginationLink.href);
        root.scrollIntoView({ behavior: 'smooth' });
      }
    }, options);

    // Browser back/forward navigation support
    window.addEventListener('popstate', (event) => {
      const popUrl = event.state?.url || window.location.href;
      applyFilters(popUrl, false);
    }, options);

    return {
      destroy() {
        abortController.abort();
        filterAbortController.abort();
        window.clearTimeout(priceDebounceTimer);
        document.body.classList.remove('overlay-open');
      }
    };
  }

  /**
   * @param {Element} root
   */
  function createFaqController(root) {
    const abortController = new AbortController();
    const options = { signal: abortController.signal };
    const searchInput = root.querySelector('[data-faq-search]');
    const chips = [...root.querySelectorAll('.faq-chips__item')];
    const groups = [...root.querySelectorAll('.faq-accordion__group')];
    const noResults = root.querySelector('[data-faq-no-results]');
    let activeChip = 'all';

    function applyFilters() {
      const query =
        searchInput instanceof HTMLInputElement
          ? searchInput.value.trim().toLowerCase()
          : '';
      let visibleCount = 0;
      groups.forEach((group) => {
        const chipMatches =
          activeChip === 'all' ||
          activeChip === group.getAttribute('data-faqgroup');
        let groupHasVisible = false;
        group.querySelectorAll('.faq-accordion__item').forEach((item) => {
          const text = item.getAttribute('data-faqtext') || '';
          const show =
            chipMatches && (query === '' || text.includes(query));
          item.toggleAttribute('hidden', !show);
          if (show) groupHasVisible = true;
        });
        group.toggleAttribute('hidden', !(groupHasVisible && chipMatches));
        if (groupHasVisible) visibleCount += 1;
      });
      noResults?.toggleAttribute('hidden', visibleCount !== 0);
    }

    chips.forEach((chip) => {
      chip.addEventListener(
        'click',
        () => {
          chips.forEach((item) => {
            item.classList.remove('faq-chips__item--active');
            item.setAttribute('aria-pressed', 'false');
          });
          chip.classList.add('faq-chips__item--active');
          chip.setAttribute('aria-pressed', 'true');
          activeChip = chip.getAttribute('data-faqchip') || 'all';
          applyFilters();
        },
        options
      );
    });
    searchInput?.addEventListener('input', applyFilters, options);

    return {
      destroy: () => abortController.abort()
    };
  }

  /**
   * @param {Element} root
   */
  function createLegalController(root) {
    const abortController = new AbortController();
    const options = { signal: abortController.signal };
    const links = [...root.querySelectorAll('.legal-page__toc-link')];
    const sections = [...root.querySelectorAll('.legal-page__section')];
    let observer = null;

    links.forEach((link) => {
      link.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          const id = link.getAttribute('data-toc-target');
          const target = id ? document.getElementById(id) : null;
          if (target) {
            target.setAttribute('tabindex', '-1');
            target.focus({ preventScroll: true });
            const url = new URL(window.location.href);
            url.hash = id;
            window.history.replaceState(window.history.state, '', url);
          }
          target?.scrollIntoView({
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
              ? 'auto'
              : 'smooth',
            block: 'start'
          });
        },
        options
      );
    });

    if ('IntersectionObserver' in window && links.length > 0 && sections.length > 0) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            links.forEach((link) => {
              link.classList.toggle(
                'legal-page__toc-link--active',
                link.getAttribute('data-toc-target') === entry.target.id
              );
            });
          });
        },
        { rootMargin: '-10% 0px -80% 0px' }
      );
      sections.forEach((section) => observer?.observe(section));
    }

    return {
      destroy() {
        abortController.abort();
        observer?.disconnect();
      }
    };
  }

  /**
   * @param {Element} root
   */
  function createNewsletterPopupController(root) {
    const abortController = new AbortController();
    const options = { signal: abortController.signal };
    let returnFocus = null;
    let showTimer = 0;

    function wasDismissed() {
      try {
        return localStorage.getItem('triple3_newsletter_popup_dismissed') === 'true';
      } catch {
        return false;
      }
    }

    function rememberDismissal() {
      try {
        localStorage.setItem('triple3_newsletter_popup_dismissed', 'true');
      } catch {
        // Storage can be unavailable in private browsing or editor previews.
      }
    }

    function show({ force = false } = {}) {
      if (!force && wasDismissed()) return;
      // Wait for the customer's current drawer or filter interaction to finish.
      if (!force && document.body.classList.contains('overlay-open')) {
        showTimer = window.setTimeout(() => show(), 1000);
        return;
      }
      returnFocus = document.activeElement;
      root.removeAttribute('hidden');
      root.removeAttribute('inert');
      root.setAttribute('aria-hidden', 'false');
      window.requestAnimationFrame(() => {
        root.classList.add('is-active');
        root
          .querySelector('[data-newsletter-popup-email], [data-newsletter-popup-close]')
          ?.focus();
      });
      document.body.classList.add('overlay-open');
    }

    function close({ remember = true } = {}) {
      const wasOpen = root.getAttribute('aria-hidden') === 'false';
      root.classList.remove('is-active');
      root.setAttribute('aria-hidden', 'true');
      root.setAttribute('inert', '');
      root.setAttribute('hidden', '');
      if (wasOpen) document.body.classList.remove('overlay-open');
      if (remember) rememberDismissal();
      if (wasOpen && returnFocus instanceof HTMLElement) returnFocus.focus();
    }

    root.querySelectorAll('[data-newsletter-popup-close]').forEach((button) => {
      button.addEventListener('click', () => close(), options);
    });
    root.addEventListener(
      'click',
      (event) => {
        if (event.target === root) close();
      },
      options
    );
    root.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape') {
          close();
          return;
        }
        if (event.key !== 'Tab') return;
        const focusable = [...root.querySelectorAll(focusableSelector)].filter(
          (element) => element.getClientRects().length > 0
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          if (last instanceof HTMLElement) last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          if (first instanceof HTMLElement) first.focus();
        }
      },
      options
    );

    const submitted =
      new URLSearchParams(window.location.search).get('customer_posted') ===
      'true';
    if (submitted) {
      try {
        localStorage.removeItem('triple3_newsletter_popup_dismissed');
      } catch {
        // Storage is optional.
      }
      show({ force: true });
    } else if (document.body.classList.contains('template-index')) {
      const delay = Math.max(
        0,
        Number(root.getAttribute('data-popup-delay-ms')) || 0
      );
      showTimer = window.setTimeout(() => show(), delay);
    }

    return {
      destroy() {
        abortController.abort();
        window.clearTimeout(showTimer);
        if (root.getAttribute('aria-hidden') === 'false') close({ remember: false });
      },
      onSectionSelect() {
        window.clearTimeout(showTimer);
        show({ force: true });
      },
      onSectionDeselect() {
        close({ remember: false });
      }
    };
  }

  /**
   * @param {Element} root
   */
  function createSlideshowEditorController(root) {
    function pause() {
      root.setAttribute('data-editor-paused', 'true');
      window.clearInterval(root._triple3Timer);
    }

    function resume() {
      root.removeAttribute('data-editor-paused');
      window.Triple3App?.startSlideshow(root);
    }

    return {
      destroy() {
        window.clearInterval(root._triple3Timer);
      },
      onSectionSelect: pause,
      onSectionDeselect: resume,
      onBlockSelect(event) {
        const selected =
          event.target instanceof Element
            ? event.target.closest('.slideshow__slide')
            : null;
        if (!selected || !root.contains(selected)) return;
        pause();
        window.Triple3App?.showSlide(
          root,
          Number(selected.getAttribute('data-index')) || 0
        );
      },
      onBlockDeselect: resume
    };
  }

  function bindLifecycle() {
    if (document.documentElement.dataset.triple3ControllersBound === 'true') return;
    document.documentElement.dataset.triple3ControllersBound = 'true';

    document.addEventListener('shopify:section:load', (event) => {
      if (event.target instanceof Element) init(event.target);
    });
    document.addEventListener('shopify:section:unload', (event) => {
      if (event.target instanceof Element) destroy(event.target);
    });
    document.addEventListener('shopify:section:select', (event) => {
      invokeForEvent(event, 'onSectionSelect');
    });
    document.addEventListener('shopify:section:deselect', (event) => {
      invokeForEvent(event, 'onSectionDeselect');
    });
    document.addEventListener('shopify:block:select', (event) => {
      invokeForEvent(event, 'onBlockSelect');
    });
    document.addEventListener('shopify:block:deselect', (event) => {
      invokeForEvent(event, 'onBlockDeselect');
    });
  }

  bindLifecycle();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), {
      once: true
    });
  } else {
    init(document);
  }
})();
